/**
 * Safe expression evaluator - prevents arbitrary code execution
 * @param {string} expression - Expression to evaluate (e.g. "1 == 1 && 2 == 2")
 * @returns {boolean} - Evaluation result
 * @throws {Error} If expression contains malicious code
 */
export const safeEval = (expression) => {
  if (typeof expression !== 'string') {
    throw new Error('Expression must be a string');
  }

  const sanitized = sanitizeExpression(expression);
  return evaluateExpression(sanitized);
};

const sanitizeExpression = (expr) => {
  // Strip quoted string contents before validation so that field values
  // (accented chars, &, |, /, etc.) don't cause false rejects.
  // Quoted content is safe: the custom parser treats it as an opaque string value.
  // Escaped quotes (\" from JSON.stringify of a field value) stay inside the string.
  const structural = expr.replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g, '""');

  const allowedChars = /^[\s\w"'()=!&|.<>+-]*$/;
  if (!allowedChars.test(structural)) {
    throw new Error('Expression contains invalid characters');
  }

  const dangerousPatterns = [
    /\bfunction\s*\(/i,
    /\bconstructor\b/i,
    /\bprototype\b/i,
    /\beval\s*\(/i,
    /\bexec\s*\(/i,
    /\bsetTimeout\s*\(/i,
    /\bsetInterval\s*\(/i,
    /\brequire\s*\(/i,
    /\bimport\b/i,
    /window\./i,
    /document\./i,
    /process\./i,
    /global\./i,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(structural)) {
      throw new Error('Expression contains forbidden pattern');
    }
  }

  return expr;
};

const evaluateExpression = (expr) => {
  const tokens = tokenize(expr.trim());
  if (tokens.length === 0) return false;
  let pos = 0;

  const parseOr = () => {
    let left = parseAnd();
    while (pos < tokens.length && tokens[pos] === '||') {
      pos++;
      const right = parseAnd();
      left = left || right;
    }
    return left;
  };

  const parseAnd = () => {
    let left = parseComparison();
    while (pos < tokens.length && tokens[pos] === '&&') {
      pos++;
      const right = parseComparison();
      left = left && right;
    }
    return left;
  };

  const parseComparison = () => {
    let left = parsePrimary();
    while (
      pos < tokens.length &&
      ['==', '===', '!=', '!==', '<', '>', '<=', '>='].includes(tokens[pos])
    ) {
      const op = tokens[pos++];
      left = compare(left, parsePrimary(), op);
    }
    return left;
  };

  const parsePrimary = () => {
    if (pos >= tokens.length) throw new Error('Unexpected end of expression');
    if (tokens[pos] === '(') {
      pos++;
      const result = parseOr();
      if (tokens[pos] !== ')') throw new Error('Mismatched parentheses');
      pos++;
      return result;
    }
    return parseValue(tokens[pos++]);
  };

  const result = parseOr();
  if (pos !== tokens.length) throw new Error('Unexpected tokens');
  return Boolean(result);
};

const tokenize = (expr) => {
  const tokens = [];
  let i = 0;

  while (i < expr.length) {
    const ch = expr[i];

    if (ch === ' ') {
      i++;
      continue;
    }

    if (ch === '(' || ch === ')') {
      tokens.push(ch);
      i++;
      continue;
    }

    if ((ch === '&' && expr[i + 1] === '&') || (ch === '|' && expr[i + 1] === '|')) {
      tokens.push(ch + expr[i + 1]);
      i += 2;
      continue;
    }

    if (ch === '"' || ch === "'") {
      const q = ch;
      let s = q;
      i++;
      while (i < expr.length && expr[i] !== q) {
        // \" or \\ inside the literal: keep the escaped char as plain text so a
        // value can never close the string and inject operators.
        if (expr[i] === '\\' && i + 1 < expr.length) {
          s += expr[i + 1];
          i += 2;
          continue;
        }
        s += expr[i++];
      }
      s += q;
      i++;
      tokens.push(s);
      continue;
    }

    if ('=!<>'.includes(ch)) {
      let op = ch;
      i++;
      if (i < expr.length && expr[i] === '=') {
        op += '=';
        i++;
        if ((ch === '=' || ch === '!') && i < expr.length && expr[i] === '=') {
          op += '=';
          i++;
        }
      }
      tokens.push(op);
      continue;
    }

    let tok = '';
    while (i < expr.length && !' ()=!<>&|"\''.includes(expr[i])) {
      tok += expr[i++];
    }
    if (tok) tokens.push(tok);
    else i++; // skip unrecognized char (e.g. lone & or |) to avoid infinite loop
  }

  return tokens;
};

const parseValue = (token) => {
  if (token === null || token === undefined) return false;
  if (token === 'true') return true;
  if (token === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(token)) return Number(token);
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    return token.slice(1, -1);
  }
  return token;
};

const compare = (left, right, operator) => {
  switch (operator) {
    case '==':
      return left == right;
    case '===':
      return left === right;
    case '!=':
      return left != right;
    case '!==':
      return left !== right;
    case '<':
      return left < right;
    case '>':
      return left > right;
    case '<=':
      return left <= right;
    case '>=':
      return left >= right;
    default:
      throw new Error(`Unknown operator: ${operator}`);
  }
};

export default safeEval;
